import { useCallback, useState } from 'react';
import { Dialog, Icons, IntegrationCallsTable, SelectUploadMediaType, TextButton } from '../../../components';
import { INTEGRATION_CALLS_MODAL_WIDTH, UPLOAD_MEDIA_TYPE_DETAILS } from '../../../constants';
import { useHandleApiResponse } from '../../../hooks';
import { useImportIntegrationCallsMutation } from '../../../services';
import { ExternalCallType, FilePurpose, IntegrationService, UploadMediaType } from '../../../types';
import { conditionalObject } from '../../../utils';
import ShareMediaLinks from './ShareMediaLinks';
import UploadMediaFiles from './UploadMediaFiles';

interface ImportRealCallsModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

const ImportRealCallsModal = ({ isOpen, setIsOpen }: ImportRealCallsModalProps) => {
  const handleApiResponse = useHandleApiResponse();
  const [importIntegrationCalls, { isLoading: isImportingCalls }] = useImportIntegrationCallsMutation();

  const [mediaType, setMediaType] = useState<UploadMediaType | undefined>();

  const handleCloseModal = useCallback(() => {
    setIsOpen(false);
    setMediaType(undefined);
  }, []);

  const handleBack = useCallback(() => {
    setMediaType(undefined);
  }, []);

  const handleImportCalls = useCallback(
    async (service: IntegrationService, selectedCallIds: string[], callType?: ExternalCallType) => {
      if (!selectedCallIds.length) return;

      try {
        const response = await importIntegrationCalls({
          service,
          externalCallIds: selectedCallIds,
          purpose: FilePurpose.CALL_CREATION,
          callType,
        });
        handleApiResponse({
          response,
          errorMsg: 'Failed to import calls',
          onSuccess: handleCloseModal,
          successMsg: 'Calls imported successfully',
        });
      } catch (error) {
        console.error(error);
      }
    },
    [importIntegrationCalls, handleApiResponse, handleCloseModal]
  );

  const {
    label: modalTitle,
    service: integrationService,
    icon: integrationIcon,
  } = mediaType
    ? UPLOAD_MEDIA_TYPE_DETAILS[mediaType]
    : {
        label: 'Import real calls',
        service: undefined,
        icon: undefined,
      };

  const integrationCallsModalProps = {
    icon: integrationIcon && <Icons icon={integrationIcon} color="text-transparent" preserveSize />,
    className: 'h-full',
    size: INTEGRATION_CALLS_MODAL_WIDTH,
  };

  return (
    <>
      <TextButton text="Import real calls" onClick={() => setIsOpen(true)} />
      <Dialog
        isOpen={isOpen}
        title={modalTitle}
        onClose={handleCloseModal}
        {...conditionalObject(!!integrationService, integrationCallsModalProps)}
      >
        {integrationService && (
          <IntegrationCallsTable
            service={integrationService}
            isLoading={isImportingCalls}
            handleImportCalls={handleImportCalls}
            onBack={handleBack}
            selectMultipleCalls
          />
        )}
        {mediaType === UploadMediaType.LINK && <ShareMediaLinks onBack={handleBack} onSuccess={handleCloseModal} />}
        {mediaType === UploadMediaType.FILE && <UploadMediaFiles onBack={handleBack} />}
        {!mediaType && <SelectUploadMediaType setMediaType={setMediaType} />}
      </Dialog>
    </>
  );
};

export default ImportRealCallsModal;
